import { useMemo } from 'react';
import styled from 'styled-components';
import {
  Column,
  FlexColumn,
  OverflowTooltip,
  Table,
} from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import useSemanticElementsData, {
  SemanticColumnRow,
} from 'shared/hooks/useSemanticElementsData';
import {
  fontSize,
  fontWeight,
  radius,
  space,
} from '@elementary/common-ui/style/utils';

const LOCAL_STORAGE_KEY = 'semantic_elements_table';

const TableWrapper = styled(FlexColumn)`
  position: relative;
  background-color: ${({ theme }) => theme.palette.surface.base};
`;

const NoElementsMessageWrapper = styled(FlexColumn)`
  border-radius: ${radius('lg')};
  padding: ${space('lg')};
  margin-top: ${space('xxxl')};
  text-align: center;
  font-size: ${fontSize('base')};
  color: ${({ theme }) => theme.palette.content.tertiary};
`;

const NoElementsMessage = () => (
  <NoElementsMessageWrapper>
    <Typography
      sx={{
        fontWeight: fontWeight('semibold'),
        fontSize: 'inherit',
        color: 'inherit',
      }}
    >
      No elements found.
    </Typography>
    <Typography sx={{ fontSize: 'inherit', color: 'inherit' }}>
      Couldn&apos;t find semantic view child elements (metrics/dimensions/facts
      etc.).
    </Typography>
  </NoElementsMessageWrapper>
);

type Props = {
  parentId: string;
};

const SemanticElementsTable = ({ parentId }: Props) => {
  const { rows, isLoading } = useSemanticElementsData(parentId);

  const columns: Column<SemanticColumnRow>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        compareFunc: (a, b) =>
          (a.displayName || a.name).localeCompare(b.displayName || b.name),
        renderer: ({ displayName, name }) => (
          <OverflowTooltip>{displayName || name}</OverflowTooltip>
        ),
      },
      {
        id: 'elementType',
        header: 'Type',
        sortable: true,
        width: '140',
        renderer: ({ elementType }) => (
          <OverflowTooltip>{elementType ?? '-'}</OverflowTooltip>
        ),
      },
      {
        id: 'dataType',
        header: 'Data type',
        sortable: true,
        width: '140',
        renderer: ({ dataType }) => (
          <OverflowTooltip>{dataType ?? '-'}</OverflowTooltip>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        sortable: false,
        renderer: ({ description }) => (
          <OverflowTooltip>{description ?? '-'}</OverflowTooltip>
        ),
      },
    ],
    [],
  );

  if (!isLoading && rows.length === 0) {
    return <NoElementsMessage />;
  }

  return (
    <TableWrapper>
      <Table
        key={parentId}
        idKey="id"
        data={rows}
        columns={columns}
        isLoading={isLoading}
        centerRowCell={false}
        localStorageKey={LOCAL_STORAGE_KEY}
        wrapperSx={{ height: '100%', maxHeight: '100%' }}
      />
    </TableWrapper>
  );
};

export default SemanticElementsTable;
